/**
 * Exercice « sur la feuille » — ce que montre le dock du bas.
 *
 * Une seule source de vérité pour le bas de l'écran :
 *  - `move`    : un Move est ouvert (choisir, débloquer, appliquer) — il REMPLACE le clavier ;
 *  - `answer`  : les touches de réponse + « Check » ;
 *  - `write`   : la palette de la feuille de brouillon (on écrit une ligne) ;
 *  - `next`    : étape de lecture sans question → « Suivant » ;
 *  - `clue`    : chasse aux indices en cours → les touches attendent ;
 *  - `sent`    : réponse envoyée (touches colorées, en lecture seule) ;
 *  - `none`    : rien (réponse calculatrice écrite sur la feuille, ou fin).
 */

export type DockMode = 'move' | 'answer' | 'write' | 'next' | 'clue' | 'sent' | 'none';

export type DockAnswerMode = 'single' | 'multiple' | 'calculator';

export interface DockInput {
  /** l'étape en cours a une question (checkpoint) */
  hasCheckpoint: boolean;
  answerMode: DockAnswerMode | null;
  /** la question en cours a déjà sa réponse */
  answered: boolean;
  /** une chasse aux indices de la question n'est pas finie */
  clueHuntPending: boolean;
  /** on peut passer à l'étape suivante (étape de lecture) */
  canAdvance: boolean;
  /** une feuille de brouillon modifiable existe pour la question */
  sheetWritable: boolean;
  /** l'élève a demandé à écrire (toucher la ligne en cours) */
  writing: boolean;
  /** un panneau de Move est ouvert : il prend toute la place du clavier */
  movePanel?: boolean;
}

export function dockMode(i: DockInput): DockMode {
  // on ne superpose pas : tant qu'un panneau de Move est ouvert, le clavier n'existe pas
  if (i.movePanel) return 'move';
  if (i.clueHuntPending && !i.answered) return 'clue';
  if (i.writing && i.sheetWritable && !i.answered) return 'write';
  if (!i.hasCheckpoint) return i.canAdvance ? 'next' : 'none';
  if (i.answerMode === 'calculator') return 'none';
  return i.answered ? 'sent' : 'answer';
}

/** Ensemble choisi après un toucher de touche (avant « Check »). */
export function toggleChoice(selected: readonly string[], id: string, multiple: boolean): string[] {
  if (multiple) {
    return selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
  }
  // réponse unique : toucher la touche déjà choisie la déchoisit
  return selected.length === 1 && selected[0] === id ? [] : [id];
}
